import {AssetType, DocumentType, PartyRole, PayoutType, TrustPurpose} from '@sollapay/enums';

import {StandardTrustType} from './trust-type.strategy';

import type {AmountDueParams, TrustScopeRequiredField} from './trust-type.strategy';
import type {Money} from '@sollapay/utils/money';

/** Default first — the toggle renders them in order. Holding two is also what makes
 * `asksSellerSideRole` true, so dropping one would silently stop the roster asking. */
const OPTIONS_SELLER_SIDE_ROLES: PartyRole[] = [PartyRole.SELLER, PartyRole.OPTION_HOLDER];

/**
 * סיחור אופציות — an option deal on its own rail: land owners and option holders on one roster,
 * one area asset entered at activation, and each assignment a purchase that links no asset.
 *
 * Every setting is stated, so a new member of `StandardTrustType` (whose defaults are
 * developer-sale-shaped) can't change this deal type unnoticed.
 */
class OptionsTrustType extends StandardTrustType {
  readonly purpose = TrustPurpose.OPTIONS;
  readonly maxCollectionPercentage = 100;

  /** Consideration and fee come from the agreements, so the price is not a ceiling on payins. */
  override readonly enforcesCollectionCap = false;

  /** The full price, whatever has been paid. */
  override computeAmountDue({maxCollectable}: AmountDueParams): Money {
    return maxCollectable;
  }

  getBuyerPartyRoles(): PartyRole[] {
    return [PartyRole.BUYER];
  }

  override readonly sellerSideRole = PartyRole.SELLER;

  override getSellerSideRoleOptions(): PartyRole[] {
    return OPTIONS_SELLER_SIDE_ROLES;
  }

  override readonly allowsMultipleSellerSideParties = true;

  override readonly asksSellerSideRelationshipQuestions = false;

  override readonly inlineAddableBeneficiaryRoles: PartyRole[] = [];

  override readonly showsBeneficiaryAddCta = true;

  override getRequiredComplianceDocumentTypes(): DocumentType[] {
    return [DocumentType.LAND_OWNERSHIP_VERIFICATION];
  }

  override getRequiredScopeFields(): TrustScopeRequiredField[] {
    return [];
  }

  override readonly requiresAssetAtActivation = true;

  /** One area asset per deal. Also what makes the shared asset readable: `findSharedAsset`
   * returns null whenever a type allows several assets. */
  override readonly allowsMultipleAssets = false;

  override getAllowedAssetTypes(): AssetType[] {
    return [AssetType.LAND];
  }

  override getDefaultAssetType(): AssetType | null {
    return AssetType.LAND;
  }

  override getAssetPropertyDocumentTypes(): DocumentType[] {
    return [DocumentType.OPTION_AGREEMENT];
  }

  /** The sale agreement annexed to the option agreement, unsigned until exercise. */
  override getOptionalAssetPropertyDocumentTypes(): DocumentType[] {
    return [DocumentType.SALE_AGREEMENT];
  }

  override readonly createsPurchaseAtActivation = false;

  override readonly allowsMultiplePurchases = true;

  /** An assignment links no asset: the land is the deal's one area asset. */
  override readonly collectsAssetPerPurchase = false;

  override readonly allowsBuyerOnMultiplePurchases = true;

  override readonly deletesPurchaseWithLastBuyer = true;

  override getRequiredPurchaseDocumentTypes(): DocumentType[] {
    return [DocumentType.ASSIGNMENT_AGREEMENT];
  }

  override getOptionalPurchaseDocumentTypes(): DocumentType[] {
    return [DocumentType.TRUSTEE_INSTRUCTIONS];
  }

  override readonly showsPayoutsTab = true;

  override readonly showsRegulatoryReports = true;

  override readonly scopesPaymentHistoryToBuyer = true;

  /** Supplier and trustee payouts need a party that can be added inline, which options doesn't offer yet. */
  override getPayoutTypeOptions(): PayoutType[] {
    return [
      PayoutType.SELLER_BANK_ACCOUNT,
      PayoutType.OPTION_HOLDER,
      PayoutType.AUTHORITIES,
      PayoutType.OTHER
    ];
  }
}

export const optionsTrustType = new OptionsTrustType();
